import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

export function useNotifications(enabled) {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const seen = useRef(new Set());
  const first = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread");
      setCount(data.count);
      setItems(data.items);
      const hasPush = !!(await getPushSubscription());
      if (!first.current && !hasPush && typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
        data.items.forEach((n) => {
          if (!seen.current.has(n.id)) {
            try { new Notification(n.title, { body: n.body }); } catch {}
          }
        });
      }
      data.items.forEach((n) => seen.current.add(n.id));
      first.current = false;
    } catch {}
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [enabled, refresh]);

  const markAllRead = useCallback(async () => {
    try { await api.post("/notifications/read-all"); setCount(0); setItems([]); } catch {}
  }, []);

  return { count, items, refresh, markAllRead };
}

export async function requestPush() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  let p = Notification.permission;
  if (p !== "granted") p = await Notification.requestPermission();
  if (p !== "granted") return p;
  const r = await subscribePush();
  return r === "subscribed" || r === "no-sw" ? "granted" : r;
}

const b64ToUint8 = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export function pushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function subscribePush() {
  if (!pushSupported()) return "no-sw";
  try {
    const { data } = await api.get("/push/vapid-public-key");
    if (!data.enabled || !data.public_key) return "disabled";
    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.public_key) });
    await api.post("/push/subscribe", { subscription: sub.toJSON(), device: navigator.userAgent.slice(0, 120) });
    return "subscribed";
  } catch (e) {
    console.error("push subscribe failed", e);
    return "error";
  }
}

export async function getPushSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/sw.js");
    return reg ? await reg.pushManager.getSubscription() : null;
  } catch { return null; }
}

export async function unsubscribePush() {
  const sub = await getPushSubscription();
  if (!sub) return;
  try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch {}
  await sub.unsubscribe();
}
